import React, { FC, useState, useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom"; // For reading query params

import { signUserUp, upsertAppUser } from "../../services/firebase";
import { signupFormAtom } from "../../state/atoms";
import { canSubmitSignupFormSelector } from "../../state/selectors";
import { Errors } from "../../types/errors";
import { LOCAL_STORAGE_KEYS } from "../../common/consts";
import { useAuthCrm } from "../../components/CrmUi/authCrm";
import {
  AuthButton,
  AuthError,
  AuthField,
  AuthLegal,
  AuthLink,
  AuthShell,
  AuthTitle,
  OnboardingPanel,
} from "./AuthUi";

/**
 * Sign up, in the look of the CRM the person came for (authCrm.ts), with the
 * onboarding call beside it. HubSpot's is measured on
 * app.hubspot.com/signup-hubspot/crm; Outreach's matches boring's Outreach
 * sign in. The CRM is saved on the user (signupCrm) for their organization.
 */
const Signup: FC = () => {
  const crm = useAuthCrm();
  const isHubSpot = crm === "hubspot";
  const [signupForm, setSignupForm] = useRecoilState(signupFormAtom);
  const canSubmitSignupForm = useRecoilValue(canSubmitSignupFormSelector);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const [outreachOrganizationId, setOutreachOrganizationId] = useState<
    string | null
  >(null);

  const [searchParams] = useSearchParams(); // Get query params

  useEffect(() => {
    const popup = searchParams.get("popup") === "true";
    const outreachOrganizationId = searchParams.get("outreachOrganizationId");

    if (popup && outreachOrganizationId) {
      setOutreachOrganizationId(outreachOrganizationId);
    }
  }, [searchParams]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (canSubmit) {
      try {
        setIsProcessing(true);
        setError("");
        // Clear stale OAuth flags so a previous failed attempt doesn't block signup
        localStorage.removeItem("oauthCodeFailed");
        localStorage.removeItem("oauthCodeFailedAt");
        localStorage.removeItem("oauthCodeProcessing");
        localStorage.removeItem("oauthCodeProcessingTime");
        localStorage.removeItem(LOCAL_STORAGE_KEYS.pendingOrganization);

        const { user } = await signUserUp(
          signupForm.email,
          signupForm.password
        );

        if (!user) {
          setError(
            `Error creating account (Code ${Errors.AUTH_SIGNUP_ERROR}). Please validate the data you have entered, and try again.`
          );
          setIsProcessing(false);
          return;
        }

        await upsertAppUser({
          uid: user.uid,
          email: user.email || signupForm.email,
          firstName: signupForm.name,
          organizationName: signupForm.organizationName,
          outreachOrganizationId: outreachOrganizationId || undefined,
          // The CRM they signed up for; the backend starts their organization on it.
          signupCrm: crm,
        });

        // Redirect to integration selection page
        window.location.href = "/app/select-integration";
      } catch (err) {
        const error = err as { message?: string } | null;
        console.error("Signup error:", error);
        setError(
          `Error creating account (Code ${Errors.AUTH_SIGNUP_FAILURE}). ${error?.message || "Please validate the data you have entered, and try again."}`
        );
        setIsProcessing(false);
      }
    }
  };

  // The organization name is required too (the form asks for it natively).
  const canSubmit = canSubmitSignupForm && signupForm.organizationName.trim().length > 0;

  const set = (field: keyof typeof signupForm) => (value: string) =>
    setSignupForm({ ...signupForm, [field]: value });

  const { password, passwordConfirmation } = signupForm;
  const isPasswordShort = password.length > 0 && password.length < 8;
  const isMismatch = passwordConfirmation.length > 0 && password !== passwordConfirmation;

  const fields = (
    <>
      <AuthField
        crm={crm}
        label={isHubSpot ? "Full name" : "Name"}
        name="name"
        autoComplete="name"
        autoFocus
        required
        value={signupForm.name}
        onChange={set("name")}
      />
      <AuthField
        crm={crm}
        label={isHubSpot ? "Company name" : "Organization name"}
        name="organization"
        autoComplete="organization"
        required
        value={signupForm.organizationName}
        onChange={set("organizationName")}
      />
      <AuthField
        crm={crm}
        label={isHubSpot ? "Work email" : "Email address"}
        type="email"
        name="email"
        autoComplete="email"
        placeholder={isHubSpot ? "name@company.com" : undefined}
        required
        value={signupForm.email}
        onChange={set("email")}
      />
      <AuthField
        crm={crm}
        label="Password"
        type="password"
        name="new-password"
        autoComplete="new-password"
        required
        minLength={8}
        hint={isPasswordShort || isHubSpot ? "At least 8 characters." : undefined}
        invalid={isPasswordShort}
        value={password}
        onChange={set("password")}
      />
      <AuthField
        crm={crm}
        label="Confirm password"
        type="password"
        name="confirm-password"
        autoComplete="new-password"
        required
        minLength={8}
        hint={isMismatch ? "The passwords don't match." : undefined}
        invalid={isMismatch}
        value={passwordConfirmation}
        onChange={set("passwordConfirmation")}
      />
      <AuthButton
        type="submit"
        loading={isProcessing}
        busyLabel="Creating your account"
        disabled={!canSubmit}
      >
        Create account
      </AuthButton>
      <AuthError>{error}</AuthError>
    </>
  );

  // The Outreach SDK's popup is small and only there to make the account.
  const aside = outreachOrganizationId ? undefined : <OnboardingPanel crm={crm} />;

  return (
    <AuthShell crm={crm} aside={aside}>
      <Helmet>
        <title>Sign up | boring</title>
      </Helmet>
      {isHubSpot ? (
        <>
          <AuthTitle>Create your account</AuthTitle>
          <p className="ba-sub">Set up boring in your HubSpot.</p>
          <form className="ba-form" onSubmit={onSubmit}>
            {fields}
          </form>
          <AuthLegal action="creating an account" />
          <p className="ba-switch">
            Have an account?{" "}
            <AuthLink crm={crm} to="/auth/login" ink>
              Sign in
            </AuthLink>
          </p>
        </>
      ) : (
        <form className="ba-form" onSubmit={onSubmit}>
          <AuthTitle>Sign up</AuthTitle>
          <p className="ba-note">Set up boring in your Outreach.</p>
          {fields}
          <AuthLegal action="creating an account" />
          <div className="ba-secondary">
            <span>
              Have an account?{" "}
              <AuthLink crm={crm} to="/auth/login">
                Sign in
              </AuthLink>
            </span>
          </div>
        </form>
      )}
    </AuthShell>
  );
};

export default Signup;
